import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Users, Trophy, ThumbsUp, CheckCircle2, AlertTriangle, Gauge, ListChecks, FileText, Loader2, Building2, Pencil, Download, ArrowLeft, Trash2 } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { DeleteJobDialog } from "../components/DeleteJobDialog";
import ResumeDropzone from "../components/ResumeDropzone";
import BatchAccordionList from "../components/BatchAccordionList";
import CandidateReportModal from "../components/CandidateReportModal";

const BUCKETS = ["Exceptional Hire", "Strong Hire", "Hire", "Borderline", "Do Not Proceed"];

export default function JobDetail() {
  const { jobId } = useParams();
  const { activeWorkspace } = useAuth();
  const navigate = useNavigate();
  const isAdmin = activeWorkspace?.role === "admin";
  const [job, setJob] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [pending, setPending] = useState([]);
  const [progress, setProgress] = useState(null);
  const [selected, setSelected] = useState(null);
  const [currentBatch, setCurrentBatch] = useState(null);
  const [openBatches, setOpenBatches] = useState([]);

  const loadJob = useCallback(() => {
    api.get(`/jobs/${jobId}`).then(({ data }) => setJob(data));
  }, [jobId]);
  const loadCandidates = useCallback(() => {
    api.get(`/jobs/${jobId}/candidates`).then(({ data }) => setCandidates(data));
  }, [jobId]);

  useEffect(() => {
    loadJob();
    loadCandidates();
  }, [loadJob, loadCandidates]);

  const handleUpload = async (files) => {
    const existingBatchNumbers = candidates.map((c) => c.batch_number || 1);
    const nextBatch = existingBatchNumbers.length ? Math.max(...existingBatchNumbers) + 1 : 1;
    setCurrentBatch(nextBatch);
    setOpenBatches((prev) => [...new Set([...prev, `batch-${nextBatch}`])]);
    setUploading(true);
    let evaluatedCount = 0;
    for (let i = 0; i < files.length; i++) {
      setPending(files.slice(i).map((f, idx) => ({ fileName: f.name, status: idx === 0 ? "processing" : "queued" })));
      setProgress({ current: i + 1, total: files.length, fileName: files[i].name });
      const formData = new FormData();
      formData.append("files", files[i]);
      formData.append("batch_number", nextBatch);
      try {
        const { data } = await api.post(`/jobs/${jobId}/candidates/upload`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setCandidates((prev) => [...prev, ...data]);
        evaluatedCount += data.length;
      } catch (err) {
        if (err.response?.status === 402) {
          toast.error("Insufficient credits — redirecting you to Billing to top up.");
          setPending([]);
          setProgress(null);
          setUploading(false);
          setCurrentBatch(null);
          navigate("/app/billing");
          return;
        }
        toast.error(`Failed to evaluate ${files[i].name}: ${formatApiError(err.response?.data?.detail)}`);
      }
    }
    setPending([]);
    setProgress(null);
    setUploading(false);
    setCurrentBatch(null);
    if (evaluatedCount > 0) toast.success(`Evaluated ${evaluatedCount} resume(s) in Batch ${nextBatch}`);
  };

  const deleteCandidate = async (candidate) => {
    try {
      await api.delete(`/jobs/${jobId}/candidates/${candidate.id}`);
      setCandidates((prev) => prev.filter((c) => c.id !== candidate.id));
      toast.success(`${candidate.candidate_name} removed`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const deleteBatch = async (batchNumber) => {
    try {
      const { data } = await api.delete(`/jobs/${jobId}/candidates/batch/${batchNumber}`);
      setCandidates((prev) => prev.filter((c) => (c.batch_number || 1) !== batchNumber));
      setOpenBatches((prev) => prev.filter((v) => v !== `batch-${batchNumber}`));
      toast.success(`Batch ${batchNumber} deleted (${data.deleted} candidate(s))`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const exportCSV = () => {
    const compNames = job.competency_matrix.map((c) => c.name);
    const header = ["Batch", "Rank", "Candidate", "File Name", "Overall Score", "Recommendation", ...compNames, "Strengths", "Major Gaps", "Hiring Verdict"];
    const sorted = [...candidates].sort((a, b) => b.overall_score - a.overall_score);
    const rows = sorted.map((c, idx) => {
      const compScores = compNames.map((name) => {
        const found = (c.competencies || []).find((x) => x.name.toLowerCase().trim() === name.toLowerCase().trim());
        return found ? found.score : "";
      });
      return [
        c.batch_number || 1,
        idx + 1,
        c.candidate_name,
        c.file_name,
        c.overall_score,
        c.recommendation,
        ...compScores,
        (c.strengths || []).join("; "),
        (c.major_gaps || []).join("; "),
        c.hiring_verdict || "",
      ];
    });
    const csvContent = [header, ...rows]
      .map((row) => row.map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${job.title.replace(/\s+/g, "_")}_rankings.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (!job) return <div className="text-sm text-muted-foreground">Loading job...</div>;

  const stats = BUCKETS.reduce((acc, b) => ({ ...acc, [b]: candidates.filter((c) => c.recommendation === b).length }), {});
  const avg = candidates.length ? Math.round(candidates.reduce((s, c) => s + c.overall_score, 0) / candidates.length) : 0;

  const batchNumbersSet = new Set(candidates.map((c) => c.batch_number || 1));
  if (uploading && currentBatch) batchNumbersSet.add(currentBatch);
  const batchNumbers = [...batchNumbersSet].sort((a, b) => b - a);
  const batches = batchNumbers.map((bn) => ({
    batchNumber: bn,
    candidates: candidates.filter((c) => (c.batch_number || 1) === bn),
    pending: uploading && bn === currentBatch ? pending : [],
    inProgress: uploading && bn === currentBatch,
  }));
  const defaultOpenBatch = batchNumbers[0] ? `batch-${batchNumbers[0]}` : null;
  const effectiveOpenBatches = openBatches.length ? openBatches : defaultOpenBatch ? [defaultOpenBatch] : [];

  return (
    <div className="animate-in fade-in-0 slide-in-from-bottom-2 duration-500">
      <Link
        to="/app"
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors duration-150"
        data-testid="job-detail-back-to-dashboard-link"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Back to Dashboard
      </Link>
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl" data-testid="job-detail-title">
            {job.title}
          </h1>
          {job.organization && (
            <p className="mt-1 flex items-center gap-1.5 text-sm font-medium text-primary" data-testid="job-detail-organization">
              <Building2 className="h-3.5 w-3.5" /> {job.organization}
            </p>
          )}
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <Link
              to={`/app/jobs/${jobId}/edit`}
              className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-sm font-medium hover:bg-muted transition-colors duration-150"
              data-testid="edit-job-link"
            >
              <Pencil className="h-3.5 w-3.5" /> Edit Job
            </Link>
            <DeleteJobDialog job={job} candidateCount={candidates.length} onDeleted={() => navigate("/app")}>
              <button
                type="button"
                className="flex items-center gap-1.5 rounded-lg border border-destructive/40 px-3 py-1.5 text-sm font-medium text-destructive transition-colors duration-150 hover:bg-destructive/10"
                data-testid="delete-job-link"
              >
                <Trash2 className="h-3.5 w-3.5" /> Delete Job
              </button>
            </DeleteJobDialog>
          </div>
        )}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Left: Job Description panel */}
        <div className="space-y-6 lg:sticky lg:top-24 lg:self-start" data-testid="job-detail-jd-panel">
          <div className="rounded-2xl border border-border/80 bg-card p-6 shadow-sm">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                <FileText className="h-4 w-4 text-primary" />
              </div>
              <h2 className="font-heading font-semibold">Job Description</h2>
            </div>
            <p className="mt-4 max-h-[420px] overflow-y-auto whitespace-pre-line text-sm leading-relaxed text-muted-foreground">
              {job.jd_text}
            </p>
          </div>

          <div className="rounded-2xl border border-border/80 bg-card p-6 shadow-sm">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                <ListChecks className="h-4 w-4 text-primary" />
              </div>
              <h2 className="font-heading font-semibold">Competency Matrix</h2>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {job.competency_matrix.map((c, i) => (
                <span key={i} className="rounded-full border border-border bg-muted/60 px-3 py-1.5 text-xs font-medium">
                  {c.name} <span className="ml-1 font-mono text-primary">{c.weight}%</span>
                </span>
              ))}
            </div>
            {job.eval_instructions && (
              <p className="mt-4 rounded-lg bg-muted/60 p-3 text-xs leading-relaxed text-muted-foreground">
                <span className="font-medium text-foreground">Instructions: </span>
                {job.eval_instructions}
              </p>
            )}
          </div>
        </div>

        {/* Right: Upload + Results panel */}
        <div className="space-y-6">
          <div className="rounded-2xl border border-border/80 bg-card p-6 shadow-sm">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                <Users className="h-4 w-4 text-primary" />
              </div>
              <h2 className="font-heading font-semibold">{candidates.length > 0 ? "Upload More Resumes" : "Upload Resumes"}</h2>
            </div>
            <div className="mt-4">
              <ResumeDropzone onUpload={handleUpload} isUploading={uploading} />
            </div>

            {progress && (
              <div className="mt-5 rounded-xl border border-primary/20 bg-primary/5 p-4" data-testid="evaluation-progress-section">
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2 font-medium text-foreground">
                    <Loader2 className="h-4 w-4 animate-spin text-primary" />
                    Evaluating <span className="max-w-[180px] truncate font-mono text-xs">{progress.fileName}</span>...
                  </span>
                  <span className="font-mono text-xs text-muted-foreground" data-testid="evaluation-progress-count">
                    {progress.current} / {progress.total}
                  </span>
                </div>
                <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-primary/15">
                  <div
                    className="h-full rounded-full bg-primary transition-all duration-500 ease-out"
                    style={{ width: `${(progress.current / progress.total) * 100}%` }}
                  />
                </div>
              </div>
            )}
          </div>

          {(candidates.length > 0 || pending.length > 0) && (
            <>
              <div className="grid grid-cols-3 gap-3" data-testid="dashboard-stats">
                <StatCard label="Total" value={candidates.length} icon={Users} />
                <StatCard label="Exceptional" value={stats["Exceptional Hire"]} icon={Trophy} tint="emerald" />
                <StatCard label="Strong Hire" value={stats["Strong Hire"]} icon={ThumbsUp} tint="sky" />
                <StatCard label="Hire" value={stats["Hire"]} icon={CheckCircle2} tint="indigo" />
                <StatCard label="Borderline" value={stats["Borderline"]} icon={AlertTriangle} tint="amber" />
                <StatCard label="Avg Score" value={avg} icon={Gauge} />
              </div>

              {candidates.length > 0 && (
                <div className="flex justify-end">
                  <button
                    onClick={exportCSV}
                    data-testid="export-rankings-csv-btn"
                    className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors duration-150"
                  >
                    <Download className="h-3.5 w-3.5" /> Export Rankings (CSV)
                  </button>
                </div>
              )}

              <BatchAccordionList
                batches={batches}
                onSelect={setSelected}
                openValues={effectiveOpenBatches}
                onOpenChange={setOpenBatches}
                onDeleteCandidate={isAdmin ? deleteCandidate : undefined}
                onDeleteBatch={isAdmin ? deleteBatch : undefined}
              />
            </>
          )}
        </div>
      </div>

      <CandidateReportModal candidate={selected} jobId={jobId} open={!!selected} onClose={() => setSelected(null)} />
    </div>
  );
}

const TINTS = {
  emerald: { bg: "bg-emerald-50 dark:bg-emerald-950/40", text: "text-emerald-600 dark:text-emerald-400" },
  sky: { bg: "bg-sky-50 dark:bg-sky-950/40", text: "text-sky-600 dark:text-sky-400" },
  indigo: { bg: "bg-sky-50 dark:bg-blue-950/40", text: "text-blue-700 dark:text-sky-400" },
  amber: { bg: "bg-amber-50 dark:bg-amber-950/40", text: "text-amber-600 dark:text-amber-400" },
};

function StatCard({ label, value, icon: Icon, tint }) {
  const t = TINTS[tint];
  return (
    <div className="rounded-xl border border-border/80 bg-card p-3 shadow-sm transition-shadow duration-200 hover:shadow-md">
      <div className={`flex h-7 w-7 items-center justify-center rounded-lg ${t?.bg || "bg-muted"}`}>
        {Icon && <Icon className={`h-3.5 w-3.5 ${t?.text || "text-muted-foreground"}`} />}
      </div>
      <div className={`mt-2 font-mono text-xl font-bold ${t?.text || "text-foreground"}`}>{value}</div>
      <div className="mt-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
    </div>
  );
}
